import { PAYMENT_METHOD } from '@monorepo/shared/schemas/common'
import type {
  CreatePayment,
  PaymentMethod,
} from '@monorepo/shared/schemas/payments'
import { format } from 'date-fns'
import { CalendarIcon, Plus } from 'lucide-react'
import { useState } from 'react'
import { paymentsService } from '@/features/payments'
import { CustomerAutocomplete } from '@/shared/components/common/CustomerAutocomplete'
import { Button } from '@/shared/components/ui/button'
import { Calendar } from '@/shared/components/ui/calendar'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'

interface AddPaymentDialogProps {
  onPaymentCreated?: () => void
  preselectedCustomerId?: string
  disabled?: boolean
}

export function AddPaymentDialog({
  onPaymentCreated,
  preselectedCustomerId,
  disabled = false,
}: AddPaymentDialogProps) {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)

  // Form state
  const [customerId, setCustomerId] = useState(preselectedCustomerId || '')
  const [amount, setAmount] = useState('')
  const [currency, setCurrency] = useState('USD')
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | ''>('')
  const [description, setDescription] = useState('')
  const [paymentDate, setPaymentDate] = useState<Date>(new Date())
  const [paymentDateOpen, setPaymentDateOpen] = useState(false)
  const [invoiceId, setInvoiceId] = useState('')

  const resetForm = () => {
    setCustomerId(preselectedCustomerId || '')
    setAmount('')
    setCurrency('USD')
    setPaymentMethod('')
    setDescription('')
    setPaymentDate(new Date())
    setInvoiceId('')
  }

  const handleSubmit = async () => {
    if (!customerId || !amount || !paymentMethod) {
      return
    }

    setLoading(true)
    try {
      const paymentData: CreatePayment = {
        customer_id: customerId,
        amount: parseFloat(amount),
        currency,
        payment_method: paymentMethod as PaymentMethod,
        payment_date: paymentDate.toISOString(),
        description: description || undefined,
        invoice_id: invoiceId || undefined,
      }

      await paymentsService.createPayment(paymentData)
      resetForm()
      setOpen(false)
      onPaymentCreated?.()
    } catch (error) {
      toast.error(extractErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }

  const isFormValid = customerId && amount && paymentMethod

  return (
    <Dialog
      open={open}
      onOpenChange={newOpen => {
        setOpen(newOpen)
        if (!newOpen) {
          resetForm()
        }
      }}
    >
      <DialogTrigger asChild>
        <Button className='gap-2' disabled={disabled}>
          <Plus className='w-4 h-4' />
          Add Payment
        </Button>
      </DialogTrigger>
      <DialogContent className='max-w-2xl'>
        <DialogHeader>
          <DialogTitle>Add Payment</DialogTitle>
          <DialogDescription>
            Record a manual payment for a customer
          </DialogDescription>
        </DialogHeader>
        <div className='space-y-4 py-4'>
          {/* Customer Selection */}
          <div className='space-y-2'>
            <Label htmlFor='customer-select'>Customer *</Label>
            <CustomerAutocomplete
              value={customerId}
              onValueChange={id => setCustomerId(id)}
              placeholder='Select a customer...'
              disabled={!!preselectedCustomerId}
            />
          </div>

          <div className='grid grid-cols-2 gap-4'>
            {/* Amount */}
            <div className='space-y-2'>
              <Label htmlFor='payment-amount'>Amount *</Label>
              <Input
                id='payment-amount'
                type='number'
                step='0.01'
                min='0'
                value={amount}
                onChange={e => setAmount(e.target.value)}
                placeholder='0.00'
              />
            </div>

            {/* Currency */}
            <div className='space-y-2'>
              <Label htmlFor='currency'>Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='USD'>USD</SelectItem>
                  <SelectItem value='EUR'>EUR</SelectItem>
                  <SelectItem value='GBP'>GBP</SelectItem>
                  <SelectItem value='CAD'>CAD</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className='grid grid-cols-2 gap-4'>
            {/* Payment Method */}
            <div className='space-y-2'>
              <Label htmlFor='payment-method'>Payment Method *</Label>
              <Select
                value={paymentMethod}
                onValueChange={value =>
                  setPaymentMethod(value as PaymentMethod)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder='Select payment method' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={PAYMENT_METHOD.credit_card}>
                    Credit Card
                  </SelectItem>
                  <SelectItem value={PAYMENT_METHOD.bank_transfer}>
                    Bank Transfer
                  </SelectItem>
                  <SelectItem value={PAYMENT_METHOD.check}>Check</SelectItem>
                  <SelectItem value={PAYMENT_METHOD.cash}>Cash</SelectItem>
                  <SelectItem value={PAYMENT_METHOD.other}>Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Payment Date */}
            <div className='space-y-2'>
              <Label htmlFor='payment-date'>Payment Date</Label>
              <Popover open={paymentDateOpen} onOpenChange={setPaymentDateOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant='outline'
                    className='w-full justify-between font-normal'
                  >
                    {paymentDate ? format(paymentDate, 'PPP') : 'Select date'}
                    <CalendarIcon className='h-4 w-4' />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className='w-auto overflow-hidden p-0'
                  align='start'
                >
                  <Calendar
                    mode='single'
                    selected={paymentDate}
                    captionLayout='dropdown'
                    onSelect={date => {
                      if (date) {
                        setPaymentDate(date)
                        setPaymentDateOpen(false)
                      }
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Description */}
          <div className='space-y-2'>
            <Label htmlFor='payment-description'>Description</Label>
            <Input
              id='payment-description'
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder='Optional payment description'
            />
          </div>

          {/* Optional Invoice ID */}
          <div className='space-y-2'>
            <Label htmlFor='invoice-id'>Invoice ID (Optional)</Label>
            <Input
              id='invoice-id'
              value={invoiceId}
              onChange={e => setInvoiceId(e.target.value)}
              placeholder='Link to specific invoice'
            />
          </div>

          {/* Actions */}
          <div className='flex justify-end gap-2 pt-4'>
            <Button
              variant='outline'
              onClick={() => setOpen(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!isFormValid || loading}>
              {loading ? 'Creating...' : 'Add Payment'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
